import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Mail, Smartphone, Link2, Loader2, Copy, Check, DollarSign, AlertCircle } from "lucide-react";

interface PaymentCollectionModalProps {
  open: boolean;
  onClose: () => void;
  workOrderId: number;
  workOrderNumber: string;
  companyName: string;
  companyEmail?: string;
  contactEmail?: string;
  contactPhone?: string;
  totalAmount: number;
  lineItems: Array<{
    description: string;
    amount: number;
    quantity: number;
  }>;
}

export default function PaymentCollectionModal({
  open,
  onClose,
  workOrderId,
  workOrderNumber,
  companyName,
  companyEmail,
  contactEmail,
  contactPhone,
  totalAmount,
  lineItems,
}: PaymentCollectionModalProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("email");
  const [emailAddress, setEmailAddress] = useState(contactEmail || companyEmail || "");
  const [phoneNumber, setPhoneNumber] = useState(contactPhone || "");
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createInvoiceMutation = useMutation({
    mutationFn: async (data: { sendMethod: "email" | "sms" | "manual"; recipient?: string }) => {
      const response = await apiRequest("POST", `/api/work-orders/${workOrderId}/send-payment-request`, data);
      return response.json();
    },
    onSuccess: (data, variables) => {
      const invalidatePaymentQueries = () => {
        queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}`] });
        queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/payment`] });
        queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/invoice-line-items`] });
      };
      
      if (variables.sendMethod === "manual") {
        setPaymentLink(data.paymentLink);
        toast({
          title: "Payment link created",
          description: "Copy the link to share with your client.",
        });
        invalidatePaymentQueries();
      } else if (variables.sendMethod === "email") {
        toast({
          title: "Invoice sent",
          description: `Payment request sent to ${emailAddress}`,
        });
        invalidatePaymentQueries();
        onClose();
      } else if (variables.sendMethod === "sms") {
        toast({
          title: "SMS sent",
          description: `Payment request sent to ${phoneNumber}`,
        });
        invalidatePaymentQueries();
        onClose();
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send payment request",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSendEmail = () => {
    if (!emailAddress) {
      toast({
        title: "Email required",
        description: "Please enter an email address.",
        variant: "destructive",
      });
      return;
    }
    createInvoiceMutation.mutate({ sendMethod: "email", recipient: emailAddress });
  };

  const handleSendSMS = () => {
    if (!phoneNumber) {
      toast({
        title: "Phone number required",
        description: "Please enter a phone number.",
        variant: "destructive",
      });
      return;
    }
    createInvoiceMutation.mutate({ sendMethod: "sms", recipient: phoneNumber });
  };

  const handleGenerateLink = () => {
    createInvoiceMutation.mutate({ sendMethod: "manual", recipient: emailAddress || undefined });
  };

  const handleCopyLink = async () => {
    if (paymentLink) {
      await navigator.clipboard.writeText(paymentLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Link copied",
        description: "Payment link copied to clipboard.",
      });
    }
  };

  const isPending = createInvoiceMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Collect Payment
          </DialogTitle>
          <DialogDescription>
            Send a payment request for work order <strong>{workOrderNumber}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-muted p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Company</span>
              <span className="font-medium">{companyName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Line Items</span>
              <span>{lineItems.length}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="font-medium">Total Amount</span>
              <Badge variant="default" className="text-base">
                ${totalAmount.toFixed(2)}
              </Badge>
            </div>
          </div>

          {lineItems.length === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                No invoice line items found. Add items to the invoice before collecting payment.
              </p>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="email" className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4" />
                  SMS
                </TabsTrigger>
                <TabsTrigger value="link" className="flex items-center gap-1.5">
                  <Link2 className="h-4 w-4" />
                  Copy Link
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="client@example.com"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    data-testid="input-payment-email"
                  />
                  <p className="text-xs text-muted-foreground">
                    A Stripe invoice will be sent to this email address.
                  </p>
                </div>
                <Button 
                  onClick={handleSendEmail} 
                  disabled={isPending || !emailAddress}
                  className="w-full"
                  data-testid="button-send-email-invoice"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Invoice via Email
                    </>
                  )}
                </Button>
              </TabsContent>

              <TabsContent value="sms" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    data-testid="input-payment-phone"
                  />
                  <p className="text-xs text-muted-foreground">
                    A payment link will be sent via SMS to this number.
                  </p>
                </div>
                <Button 
                  onClick={handleSendSMS} 
                  disabled={isPending || !phoneNumber}
                  className="w-full"
                  data-testid="button-send-sms-invoice"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Smartphone className="mr-2 h-4 w-4" />
                      Send Payment Link via SMS
                    </>
                  )}
                </Button>
              </TabsContent>

              <TabsContent value="link" className="space-y-4 pt-4">
                {paymentLink ? (
                  <div className="space-y-3">
                    <Label>Payment Link</Label>
                    <div className="flex gap-2">
                      <Input
                        value={paymentLink}
                        readOnly
                        className="flex-1 text-sm"
                        data-testid="input-payment-link"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyLink}
                        data-testid="button-copy-link"
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Share this link with your client to collect payment.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Generate a payment link to share manually with your client via any channel.
                    </p>
                    <Button 
                      onClick={handleGenerateLink} 
                      disabled={isPending}
                      className="w-full"
                      data-testid="button-generate-link"
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Link2 className="mr-2 h-4 w-4" />
                          Generate Payment Link
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
